import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';

// ── Formatting helpers ──────────────────────────────────────────────────────
export const formatMoney = (value, currency) => {
    const n = value != null && !isNaN(Number(value)) ? Number(value) : 0;
    const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${formatted} ${currency}` : formatted;
};

export const formatInt = (n) => Number(n || 0).toLocaleString('en-US');

export const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const timeAgo = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    const sec = Math.round((Date.now() - d.getTime()) / 1000);
    if (sec < 45) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day}d ago`;
    return formatDateTime(value);
};

export const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const AVATAR_PALETTE = [
    ['#e0f2fe', '#0369a1'],
    ['#ede9fe', '#6d28d9'],
    ['#dcfce7', '#15803d'],
    ['#fef3c7', '#b45309'],
    ['#fce7f3', '#be185d'],
    ['#e0e7ff', '#3730a3'],
    ['#ffe4e6', '#be123c'],
    ['#cffafe', '#0e7490'],
];

const avatarPalette = (seed) => {
    const s = String(seed || '');
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

// ── Transaction type → label/icon/tone map (shared by ledger + supervisor views) ──
export const TXN_TYPE_META = {
    COD_DEBIT: { label: 'Collected COD', icon: 'fa-arrow-down', tone: 'warning' },
    SETTLEMENT_CREDIT: { label: 'Handed over to supervisor', icon: 'fa-arrow-up', tone: 'primary' },
    HANDOVER_TO_FINANCE: { label: 'Handover to finance', icon: 'fa-university', tone: 'primary' },
    ADJUSTMENT: { label: 'Adjustment', icon: 'fa-wrench', tone: 'neutral' },
};

export const txnTypeMeta = (type) => TXN_TYPE_META[type] || { label: type || '-', icon: 'fa-exchange', tone: 'neutral' };

// ── Responsive helper ───────────────────────────────────────────────────────
// Tracks a max-width media query so pages can trim secondary table columns /
// rearrange layout for phones instead of relying on horizontal scroll alone.
export function useIsMobile(breakpoint = 720) {
    const query = `(max-width: ${breakpoint}px)`;
    const [isMobile, setIsMobile] = useState(() => (
        typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
    ));

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia(query);
        const onChange = (e) => setIsMobile(e.matches);
        setIsMobile(mql.matches);
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
        };
    }, [query]);

    return isMobile;
}

// ── Searchable dropdown (react-select themed to match the wallet design system) ──
// Every option list gets built-in type-to-search, keyboard nav and a11y for free;
// menus render in a body portal so they're never clipped by modal/card overflow.
const walletSelectStyles = (small) => ({
    container: (base) => ({ ...base, width: '100%' }),
    control: (base, state) => ({
        ...base,
        minHeight: small ? 34 : 38,
        borderRadius: 9,
        borderColor: state.isFocused ? 'var(--w-primary)' : 'var(--w-border)',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 166, 237, .12)' : 'none',
        background: state.isFocused ? '#fff' : '#fbfcfe',
        fontSize: 13.5,
        fontFamily: 'inherit',
        cursor: 'pointer',
        '&:hover': { borderColor: 'var(--w-primary)' },
    }),
    valueContainer: (base) => ({ ...base, padding: small ? '2px 10px' : '4px 12px' }),
    placeholder: (base) => ({ ...base, color: 'var(--w-muted)', fontSize: 13.5 }),
    singleValue: (base) => ({ ...base, color: 'var(--w-ink)' }),
    input: (base) => ({ ...base, color: 'var(--w-ink)', fontFamily: 'inherit' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: 'var(--w-muted)', padding: 6 }),
    clearIndicator: (base) => ({ ...base, color: 'var(--w-muted)', padding: 6, '&:hover': { color: 'var(--w-danger)' } }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
        ...base,
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid var(--w-border)',
        boxShadow: '0 10px 30px rgba(16, 24, 40, .15)',
        fontFamily: 'inherit',
    }),
    menuList: (base) => ({ ...base, padding: 4, maxHeight: 260 }),
    option: (base, state) => ({
        ...base,
        borderRadius: 7,
        fontSize: 13.5,
        padding: '8px 10px',
        background: state.isSelected ? 'var(--w-primary)' : state.isFocused ? 'var(--w-primary-light)' : '#fff',
        color: state.isSelected ? '#fff' : 'var(--w-ink)',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        opacity: state.isDisabled ? .55 : 1,
    }),
    noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: 'var(--w-muted)' }),
});

export function WalletSelect({ small, ...props }) {
    return (
        <Select
            classNamePrefix="wsel"
            styles={walletSelectStyles(small)}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            isSearchable
            {...props}
        />
    );
}

// ── Small presentational components ─────────────────────────────────────────
export function Avatar({ name, size = 36 }) {
    const [bg, fg] = avatarPalette(name);
    return (
        <div className="w-avatar" style={{ width: size, height: size, background: bg, color: fg, fontSize: Math.round(size * 0.38) }}>
            {initials(name)}
        </div>
    );
}

export function Person({ name, sub, size = 36 }) {
    return (
        <div className="w-person">
            <Avatar name={name} size={size} />
            <div>
                <div className="w-person-name">{name || 'Unknown'}</div>
                {sub ? <div className="w-person-sub">{sub}</div> : null}
            </div>
        </div>
    );
}

export function Pill({ tone = 'neutral', icon, children }) {
    return (
        <span className={`w-pill w-pill-${tone}`}>
            {icon ? <i className={`fa ${icon}`} aria-hidden="true" /> : null}
            {children}
        </span>
    );
}

export function StatCard({ icon, label, value, tone = 'neutral', accent = false, sub, action }) {
    return (
        <div className={`w-stat w-stat-${tone} ${accent ? 'w-stat-accent' : ''}`}>
            <div className="w-stat-icon"><i className={`fa ${icon}`} aria-hidden="true" /></div>
            <div className="w-stat-body">
                <div className="w-stat-label">{label}</div>
                <div className="w-stat-value">{value}</div>
                {sub ? <div className="w-stat-sub">{sub}</div> : null}
                {action ? <div style={{ marginTop: 10 }}>{action}</div> : null}
            </div>
        </div>
    );
}

export function EmptyState({ icon = 'fa-inbox', title = 'Nothing here yet', subtitle }) {
    return (
        <div className="w-empty">
            <i className={`fa ${icon}`} aria-hidden="true" />
            <p>{title}</p>
            {subtitle ? <span>{subtitle}</span> : null}
        </div>
    );
}

export function WalletSpinner({ label = 'Loading…' }) {
    return <div className="w-loading"><i className="fa fa-spinner fa-spin" aria-hidden="true" /> {label}</div>;
}

export function IconButton({ icon, tone, onClick, disabled, title, size }) {
    return (
        <button type="button" className={`w-icon-btn ${tone ? `tone-${tone}` : ''}`} onClick={onClick} disabled={disabled}
                title={title} aria-label={title} style={size ? { width: size, height: size } : undefined}>
            <i className={`fa ${icon}`} aria-hidden="true" />
        </button>
    );
}

// ── Toast notifications (replaces alert()/window.confirm-style banners) ────
let toastSeq = 0;

export function useWalletToasts() {
    const [toasts, setToasts] = useState([]);
    const timers = useRef({});

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
        if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    }, []);

    const push = useCallback((type, text, opts = {}) => {
        const id = ++toastSeq;
        const duration = opts.duration != null ? opts.duration : (type === 'error' ? 5500 : 3500);
        setToasts(prev => [...prev, { id, type, text }]);
        if (duration > 0) timers.current[id] = setTimeout(() => dismiss(id), duration);
        return id;
    }, [dismiss]);

    const toast = {
        success: (text, opts) => push('success', text, opts),
        error: (text, opts) => push('error', text, opts),
        info: (text, opts) => push('info', text, opts),
    };

    return { toasts, toast, dismiss };
}

const TOAST_ICON = { success: 'fa-check-circle', error: 'fa-exclamation-circle', info: 'fa-info-circle' };

export function WalletToastStack({ toasts, onDismiss }) {
    if (!toasts || !toasts.length) return null;
    return (
        <div className="w-toast-stack">
            {toasts.map(t => (
                <div key={t.id} className={`w-toast w-toast-${t.type}`} role="alert">
                    <i className={`fa ${TOAST_ICON[t.type] || 'fa-info-circle'}`} aria-hidden="true" />
                    <span>{t.text}</span>
                    <button type="button" className="w-toast-close" onClick={() => onDismiss(t.id)} aria-label="Dismiss">
                        <i className="fa fa-times" aria-hidden="true" />
                    </button>
                </div>
            ))}
        </div>
    );
}

// ── Drawn-signature pad (mouse + touch), exports a base64 PNG ─────────────────
export function SignaturePad({ onChange, hint = 'Sign above to confirm.' }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const dirty = useRef(false);

    const ctx = () => {
        const c = canvasRef.current;
        const g = c.getContext('2d');
        g.strokeStyle = '#111';
        g.lineWidth = 2;
        g.lineJoin = 'round';
        g.lineCap = 'round';
        return g;
    };
    const pos = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const src = e.touches ? e.touches[0] : e;
        return { x: (src.clientX - r.left) * (c.width / r.width), y: (src.clientY - r.top) * (c.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); const g = ctx(); g.beginPath(); g.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); const g = ctx(); g.lineTo(p.x, p.y); g.stroke(); dirty.current = true; };
    const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(canvasRef.current.toDataURL('image/png')); };
    const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(''); };

    return (
        <div className="w-sig-wrap">
            <canvas ref={canvasRef} width={440} height={150}
                style={{ border: '1px dashed #adb5bd', width: '100%', height: 150, touchAction: 'none', background: '#fff' }}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
            <div className="w-sig-actions">
                <span style={{ fontSize: 12, color: '#6b7280' }}><i className="fa fa-info-circle" aria-hidden="true" /> {hint}</span>
                <button type="button" className="w-btn w-btn-ghost w-btn-sm" onClick={clear}><i className="fa fa-eraser" aria-hidden="true" /> Clear</button>
            </div>
        </div>
    );
}
